import type { ApiError } from "@circle-react/config/CustomErrors";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { getIsCommunityMember } from "@circle-react/helpers/audienceHelpers";
import {
  reactQueryGet,
  reactQueryPut,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { AudienceType } from "@circle-react/types/AudienceList";
import type { CommunityMember } from "@circle-react/types/CommunityMember";
import { useMutation, useQuery, useQueryClient } from "react-query";
import invariant from "tiny-invariant";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import type { UserInformationField } from "@/react/components/shared/ProfileFields/FormFields/UserInformation/types";
import type { MemberSocialLinks } from "@/react/helpers/memberHelper";

export const PROFILE_INFO_QUERY_KEY = "PROFILE_INFO";

interface InvitationStatusHistory {
  account_created: string | null;
  invite_opened: string | null;
  invited: string | null;
  profile_completed: string | null;
}

interface ProfileInvitation {
  invitation_status: string | null;
  /** Present on some profile payloads; omitted on others. */
  invitation_status_history?: InvitationStatusHistory;
  is_invitation_onboarding_flow: boolean | null;
}

interface ProfileCommunication {
  can_receive_dm_from_current_member?: boolean;
  has_blocked_current_community_member?: boolean;
  is_blocked_by_current_community_member?: boolean;
  make_my_email_public?: boolean;
  messaging_enabled_by_member?: boolean;
}

interface ProfileActivityCounts {
  comments_count?: number;
  last_seen_at?: string | null;
  member_since?: string;
  posts_count?: number;
  spaces_count?: number;
}

/** From `internalApi.profile.show` (CommunityMember) — extends base member with drawer/full-page fields. */
export type ProfileResponse = CommunityMember &
  MemberSocialLinks &
  ProfileInvitation &
  ProfileCommunication &
  ProfileActivityCounts & {
    avatar?: string | null;
    created_at?: string;
    has_active_paywall_subscriptions?: boolean;
    is_deleted: boolean;
    locale?: string;
    moderator?: boolean;
    profile_fields: {
      visible: UserInformationField[];
      not_visible: UserInformationField[];
    };
    profile_url?: string;
    status?: string;
    subscribed_to_marketing_emails?: boolean;
    time_zone?: string;
  };

export const useProfileInfo = ({
  publicId,
  params = {},
  type = "CommunityMember",
}: {
  publicId?: string;
  params?: Record<string, unknown>;
  type?: AudienceType;
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { refetch: refetchPunditUserContext } = usePunditUserContext();
  const queryKey = [PROFILE_INFO_QUERY_KEY, publicId, params];

  const fetchMemberProfileInfo = () =>
    reactQueryGet(
      internalApi.profile.show({ memberPublicId: publicId, params }),
    );

  const fetchLeadProfileInfo = () =>
    reactQueryGet(
      internalApi.profile.leadShow({ leadPublicId: publicId, params }),
    );

  const getProfileInfoQuery = useQuery<ProfileResponse, ApiError>({
    queryKey,
    queryFn: getIsCommunityMember({ type })
      ? fetchMemberProfileInfo
      : fetchLeadProfileInfo,
    retry: false,
    enabled: Boolean(publicId),
  });

  const updateProfileInfoMutation = useMutation<unknown, ApiError, any>({
    mutationFn: formData =>
      reactQueryPut(
        internalApi.profile.update({
          memberPublicId: publicId,
        }),
        formData,
      ),
    onSuccess: async () => {
      const currentProfileInfo = getProfileInfoQuery?.data;
      const currentLocale = currentProfileInfo?.locale;

      toast.success(t("profile_saved_successfully"));
      await refetchPunditUserContext();
      await queryClient.invalidateQueries({
        queryKey: [PROFILE_INFO_QUERY_KEY],
      });

      const newProfileInfo = await queryClient.fetchQuery({
        queryKey,
        queryFn: fetchMemberProfileInfo,
      });
      const newLocale = newProfileInfo?.locale;

      window.webview?.onProfileInfoSaved?.();
      // reload the page if the locale has changed
      if (currentLocale !== newLocale) {
        window.location.reload();
      }
    },
    onError: err => {
      toast.error(err.message);
    },
  });

  return {
    getProfileInfoQuery,
    updateProfileInfoMutation,
  };
};

export const useUpdateProfileInfo = ({ publicId }: { publicId: string }) => {
  if (!publicId) {
    invariant(false, "Public ID is required");
  }

  const queryClient = useQueryClient();
  const { refetch: refetchPunditUserContext } = usePunditUserContext();

  return useMutation<unknown, ApiError, any>({
    mutationFn: formData =>
      reactQueryPut(
        internalApi.profile.update({
          memberPublicId: publicId,
        }),
        formData,
      ),
    onSuccess: async () => {
      await refetchPunditUserContext();
      await queryClient.invalidateQueries({
        queryKey: [PROFILE_INFO_QUERY_KEY],
      });
      window.webview?.onProfileInfoSaved?.();
    },
  });
};
